import { sql } from 'drizzle-orm';
import {
  index,
  uniqueIndex,
  jsonb,
  pgTable,
  timestamp,
  varchar,
  integer,
  text,
  boolean,
  real,
  uuid, 
  date
} from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

export const EXPERIENCE_LEVELS = ['beginner', 'intermediate', 'advanced'] as const;
export type ExperienceLevel = typeof EXPERIENCE_LEVELS[number];

export const SUBSCRIPTION_STATUSES = ['inactive', 'trial', 'active', 'past_due', 'canceled'] as const;
export type SubscriptionStatus = typeof SUBSCRIPTION_STATUSES[number];

export const USER_ROLES = ['user', 'beta', 'subscriber', 'coach', 'admin'] as const;
export type UserRole = typeof USER_ROLES[number];

export const BARBELL_ENTRY_MODES = ['total', 'total_added', 'per_side'] as const;
export type BarbellEntryMode = typeof BARBELL_ENTRY_MODES[number];

export const PROGRESSION_FREQUENCIES = ['every_session', 'every_2_sessions', 'every_3_sessions', 'weekly', 'every_2_weeks', 'off'] as const;
export type ProgressionFrequency = typeof PROGRESSION_FREQUENCIES[number];

export const MAX_DUMBBELL_MODES = ['global', 'per_program'] as const;
export type MaxDumbbellMode = typeof MAX_DUMBBELL_MODES[number];

export const SIGNUP_STATUSES = ['pending_payment', 'active', 'canceled'] as const;
export type SignupStatus = typeof SIGNUP_STATUSES[number];

export const SUPER_ADMIN_EMAILS = ['glebber@gmail.com', 'dr.gleb.kuzmin@gmail.com'] as const;

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  supabaseId: varchar("supabase_id").unique(),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  role: varchar("role").notNull().default("user"),
  isCoach: boolean("is_coach").notNull().default(false),
  subscriptionStatus: varchar("subscription_status").notNull().default("inactive"),
  weightUnit: varchar("weight_unit").notNull().default("kg"),
  distanceUnit: varchar("distance_unit").notNull().default("km"),
  musicGenre: varchar("music_genre").notNull().default("hip-hop"),
  experienceLevel: varchar("experience_level"),
  barbellWeight: real("barbell_weight").notNull().default(20),
  barbellEntryMode: varchar("barbell_entry_mode").notNull().default("total"),
  barbellIncrement: real("barbell_increment"),
  dumbbellIncrement: real("dumbbell_increment"),
  maxDumbbellWeight: real("max_dumbbell_weight"),
  maxDumbbellMode: varchar("max_dumbbell_mode").notNull().default("global"),
  progressionFrequency: varchar("progression_frequency").notNull().default("every_session"),
  progressionMode: varchar("progression_mode").notNull().default("double"),
  rirEnabled: boolean("rir_enabled").notNull().default(true),
  rirProgressThreshold: integer("rir_progress_threshold").notNull().default(4),
  rirDeloadThreshold: integer("rir_deload_threshold").notNull().default(1),
  stripeCustomerId: varchar("stripe_customer_id"),
  stripeSubscriptionId: varchar("stripe_subscription_id"),
  trialStartedAt: timestamp("trial_started_at"),
  trialEndsAt: timestamp("trial_ends_at"),
  subscriptionEndsAt: timestamp("subscription_ends_at"),
  accessCodeUsed: varchar("access_code_used"),
  avatarId: varchar("avatar_id"),
  authProvider: varchar("auth_provider").default("email"),
  emailProductUpdates: boolean("email_product_updates").notNull().default(true),
  emailWorkoutReminders: boolean("email_workout_reminders").notNull().default(false),
  emailMarketing: boolean("email_marketing").notNull().default(false),
  workoutReminderTime: varchar("workout_reminder_time").notNull().default("08:00"),
  hideInstallPrompt: boolean("hide_install_prompt").notNull().default(false),
  themeMode: varchar("theme_mode").notNull().default("auto"),
  signupStatus: varchar("signup_status").notNull().default("active"),
  pendingCheckoutSessionId: varchar("pending_checkout_session_id"),
  signupPromoCode: varchar("signup_promo_code"),
  hiddenCuratedGenres: text("hidden_curated_genres").array().default(sql`'{}'::text[]`),
  customPlaylists: jsonb("custom_playlists").default(sql`'[]'::jsonb`),
  celebratedBlockIds: text("celebrated_block_ids").array().default(sql`'{}'::text[]`),
  hiddenBlockIds: text("hidden_block_ids").array().default(sql`'{}'::text[]`),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("IDX_users_role").on(table.role),
  index("IDX_users_created_at").on(table.createdAt),
  index("IDX_users_supabase_id").on(table.supabaseId),
  index("IDX_users_subscription_status").on(table.subscriptionStatus),
  index("IDX_users_signup_status").on(table.signupStatus),
]);

export const ACCESS_CODE_TYPES = ['beta', 'trial', 'promo', 'lifetime'] as const;
export type AccessCodeType = typeof ACCESS_CODE_TYPES[number];

export const accessCodes = pgTable("access_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: varchar("code").notNull().unique(),
  type: varchar("type").notNull().default("trial"), // "beta", "trial", "promo", "lifetime"
  maxUses: integer("max_uses"),
  currentUses: integer("current_uses").notNull().default(0),
  expiresAt: timestamp("expires_at"),
  durationDays: integer("duration_days").notNull().default(7), // days of free access
  discountPercent: integer("discount_percent"), // for promo codes: 10 = 10% off, 100 = free
  stripePromotionCode: varchar("stripe_promotion_code"), // Stripe promo code ID for subscription discounts
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const accessCodeRedemptions = pgTable("access_code_redemptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  accessCodeId: varchar("access_code_id").references(() => accessCodes.id).notNull(),
  redeemedAt: timestamp("redeemed_at").defaultNow(),
  expiresAt: timestamp("expires_at").notNull(),
}, (table) => [
  index("IDX_access_code_redemptions_user").on(table.userId),
  index("IDX_access_code_redemptions_code_date").on(table.accessCodeId, table.redeemedAt),
]);

export const PROGRAM_TYPES = ['expert', 'custom', 'template'] as const;
export type ProgramType = typeof PROGRAM_TYPES[number];

export const BADGE_STATUS = ['new', 'updated'] as const;
export type BadgeStatus = typeof BADGE_STATUS[number];

export const PROGRAM_EXPERIENCE_LEVELS = ["beginner", "intermediate", "advanced", "all levels"] as const;
export type ProgramExperienceLevel = typeof PROGRAM_EXPERIENCE_LEVELS[number];

export const MUSCLE_GROUPS = [
  "full body", "upper body", "lower body", "push", "pull", "legs",
  "chest", "back", "shoulders", "biceps", "triceps", "forearms",
  "core", "glutes", "quadriceps", "hamstrings", "calves"
] as const;
export type MuscleGroup = typeof MUSCLE_GROUPS[number];

export const programs = pgTable("programs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  title: varchar("title").notNull(),
  description: text("description").notNull(),
  difficulty: varchar("difficulty").notNull(),
  duration: varchar("duration").notNull(),
  totalWeeks: integer("total_weeks").notNull(),
  imageUrl: varchar("image_url"),
  spotifyPlaylistUrl: varchar("spotify_playlist_url"),
  // Curated playlists for different workout phases (admin-assigned)
  curatedWarmupPlaylist: varchar("curated_warmup_playlist"),
  curatedMainPlaylist: varchar("curated_main_playlist"),
  curatedCooldownPlaylist: varchar("curated_cooldown_playlist"),
  usePerSectionPlaylists: boolean("use_per_section_playlists").notNull().default(false), // If true, use section-level playlists
  status: varchar("status").notNull().default("draft"),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  programType: varchar("program_type").notNull().default("custom"),
  versionNumber: integer("version_number").notNull().default(1),
  versionGroupId: varchar("version_group_id"),
  publishedAt: timestamp("published_at"),
  contentUpdatedAt: timestamp("content_updated_at"), // Auto-set when program content is edited
  badgeStatus: varchar("badge_status"),
  supersedesProgramId: varchar("supersedes_program_id"),
  experienceLevel: varchar("experience_level"),
  muscleGroups: text("muscle_groups").array(),
  recommendedPlaylistId: varchar("recommended_playlist_id"),
  isPrivate: boolean("is_private").notNull().default(false), // If true, only visible to creator
}, (table) => [
  index("IDX_programs_status").on(table.status),
  index("IDX_programs_program_type").on(table.programType),
  index("IDX_programs_created_by").on(table.createdBy),
  index("IDX_programs_type_status").on(table.programType, table.status),
  index("IDX_programs_version_group").on(table.versionGroupId),
  index("IDX_programs_is_private").on(table.isPrivate),
]);

export const workoutSections = pgTable("workout_sections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  programId: varchar("program_id").references(() => programs.id).notNull(),
  title: varchar("title").notNull(),
  duration: varchar("duration").notNull(),
  spotifyLink: varchar("spotify_link"),
  // Section-level curated playlists (optional overrides, admin-assigned)
  curatedWarmupPlaylist: varchar("curated_warmup_playlist"),
  curatedMainPlaylist: varchar("curated_main_playlist"),
  curatedCooldownPlaylist: varchar("curated_cooldown_playlist"),
  inheritProgramPlaylists: boolean("inherit_program_playlists").notNull().default(true), // If true, use program's playlists
  order: integer("order").notNull(),
  // For multi-session programs (e.g., Push/Pull/Legs with different workouts per day)
  sessionId: varchar("session_id"), // e.g., "push", "pull", "legs"
  sessionName: varchar("session_name"), // e.g., "Push Day", "Pull Day", "Leg Day"
  // Recommended playlist for this session (energy level ID or null)
  recommendedPlaylistId: varchar("recommended_playlist_id"),
}, (table) => [
  index("IDX_workout_sections_program").on(table.programId),
]);

export const EXERCISE_TRACKING_TYPES = ['strength', 'time', 'cardio'] as const;
export type ExerciseTrackingType = typeof EXERCISE_TRACKING_TYPES[number];

export const EXERCISE_CATEGORIES = [
  'compound',
  'chest',
  'back',
  'shoulders',
  'biceps',
  'triceps',
  'legs_quads',
  'legs_hamstrings_glutes',
  'calves',
  'core',
  'cardio',
  'mobility',
  'forearms'
] as const;
export type ExerciseCategory = typeof EXERCISE_CATEGORIES[number];

export const EQUIPMENT_TYPES = [
  'barbell',
  'dumbbell',
  'machine',
  'cable',
  'bodyweight',
  'kettlebell',
  'bands',
  'smith_machine',
  'other',
  'none',
  // Cardio equipment
  'treadmill',
  'stationary_bike',
  'elliptical',
  'rowing_machine',
  'stair_climber',
  'ski_erg',
  'assault_bike',
  // Body part targets for stretching/mobility exercises
  'full_body',
  'upper_body',
  'lower_body',
  'shoulders',
  'back',
  'chest',
  'hips',
  'legs',
  'arms',
  'core',
  'neck'
] as const;
export type EquipmentType = typeof EQUIPMENT_TYPES[number];

// Equipment types that can be used for strength exercises (logged per-set)
export const STRENGTH_EQUIPMENT_TYPES = [
  'barbell',
  'dumbbell',
  'machine',
  'cable',
  'bodyweight',
  'kettlebell',
  'bands',
  'smith_machine',
  'other'
] as const;
export type StrengthEquipmentType = typeof STRENGTH_EQUIPMENT_TYPES[number];

export const masterExercises = pgTable("master_exercises", {
  id: varchar("id").primaryKey(),
  name: varchar("name").notNull(),
  category: varchar("category").notNull(),
  trackingType: varchar("tracking_type").notNull().default("strength"),
  defaultEquipment: varchar("default_equipment").array(),
  description: text("description"),
  musclesWorked: text("muscles_worked").array(),
  aliases: text("aliases").array(),
  isCompound: boolean("is_compound").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  status: varchar("status").notNull().default("published"),
  isReverseProgression: boolean("is_reverse_progression").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("IDX_master_exercises_category").on(table.category),
  index("IDX_master_exercises_tracking_type").on(table.trackingType),
  index("IDX_master_exercises_status").on(table.status),
]);

export const insertMasterExerciseSchema = createInsertSchema(masterExercises);
export type InsertMasterExercise = z.infer<typeof insertMasterExerciseSchema>;
export type MasterExercise = typeof masterExercises.$inferSelect;

// User's personal exercise library - exercises they can use across all programs
// Structure mirrors master_exercises for consistency
export const userExercises = pgTable("user_exercises", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  name: varchar("name").notNull(),
  category: varchar("category").notNull().default("compound"),
  trackingType: varchar("tracking_type").notNull().default("strength"),
  compatibleEquipment: text("compatible_equipment").array().default(sql`'{bodyweight}'::text[]`), // Equipment this exercise can be done with
  defaultSets: integer("default_sets").notNull().default(3),
  defaultReps: varchar("default_reps").notNull().default("10-12"),
  description: text("description"),
  musclesWorked: text("muscles_worked").array(),
  isCompound: boolean("is_compound").notNull().default(false),
  isReverseProgression: boolean("is_reverse_progression").notNull().default(false),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("IDX_user_exercises_user").on(table.userId),
  index("IDX_user_exercises_category").on(table.category),
  index("IDX_user_exercises_name").on(table.userId, table.name),
]);

export const insertUserExerciseSchema = createInsertSchema(userExercises).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertUserExercise = z.infer<typeof insertUserExerciseSchema>;
export type UserExercise = typeof userExercises.$inferSelect;

export const exercises = pgTable("exercises", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sectionId: varchar("section_id").references(() => workoutSections.id).notNull(),
  name: varchar("name").notNull(),
  equipment: varchar("equipment").notNull(),
  sets: integer("sets").notNull(),
  reps: varchar("reps").notNull(),
  minReps: integer("min_reps"),
  maxReps: integer("max_reps"),
  notes: text("notes"),
  order: integer("order").notNull(),
  trackingType: varchar("tracking_type").notNull().default("strength"),
  masterExerciseId: varchar("master_exercise_id").references(() => masterExercises.id),
}, (table) => [
  index("IDX_exercises_section").on(table.sectionId),
  index("IDX_exercises_master").on(table.masterExerciseId),
]);

export const userPrograms = pgTable("user_programs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  programId: varchar("program_id").references(() => programs.id).notNull(),
  currentWeek: integer("current_week").notNull().default(1),
  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  status: varchar("status").notNull().default("current"),
  isFavorite: boolean("is_favorite").notNull().default(false),
  source: varchar("source").notNull().default("expert"),
  maxDumbbellWeight: real("max_dumbbell_weight"),
}, (table) => [
  index("IDX_user_programs_user").on(table.userId),
  index("IDX_user_programs_user_program").on(table.userId, table.programId),
  index("IDX_user_programs_status").on(table.status),
  index("IDX_user_programs_user_status").on(table.userId, table.status),
]);

export const workoutLogs = pgTable("workout_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  exerciseId: varchar("exercise_id"),
  programId: varchar("program_id"),
  week: integer("week").notNull(),
  setNumber: integer("set_number").notNull(),
  weight: varchar("weight"),
  reps: varchar("reps"),
  completed: boolean("completed").notNull().default(false),
  loggedAt: timestamp("logged_at").defaultNow(),
  distance: real("distance"),
  durationSeconds: integer("duration_seconds"),
  paceSeconds: integer("pace_seconds"),
  distanceUnitSnapshot: varchar("distance_unit_snapshot"),
  weightUnitSnapshot: varchar("weight_unit_snapshot"),
  equipmentSnapshot: varchar("equipment_snapshot"),
  barbellEntryModeSnapshot: varchar("barbell_entry_mode_snapshot"),
  exerciseNameSnapshot: varchar("exercise_name_snapshot"),
  programNameSnapshot: varchar("program_name_snapshot"),
  sectionNameSnapshot: varchar("section_name_snapshot"),
  masterExerciseId: varchar("master_exercise_id"),
}, (table) => [
  index("IDX_workout_logs_user").on(table.userId),
  index("IDX_workout_logs_user_program_week").on(table.userId, table.programId, table.week),
  index("IDX_workout_logs_user_date").on(table.userId, table.loggedAt),
  index("IDX_workout_logs_exercise").on(table.exerciseId),
  index("IDX_workout_logs_date").on(table.loggedAt),
  index("IDX_workout_logs_master_exercise").on(table.masterExerciseId),
]);

export const bodyWeightLogs = pgTable("body_weight_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  weight: real("weight").notNull(),
  loggedAt: timestamp("logged_at").defaultNow(),
}, (table) => [
  index("IDX_body_weight_logs_user").on(table.userId),
  index("IDX_body_weight_logs_user_date").on(table.userId, table.loggedAt),
]);

// ============================================================
// WORKOUT_SETS - Canonical per-set exercise logging
// Equipment is recorded per-set, not per-exercise
// ============================================================

export const EXERCISE_SOURCES = ['master', 'user'] as const;
export type ExerciseSource = typeof EXERCISE_SOURCES[number];

export const workoutSets = pgTable("workout_sets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  
  // Canonical exercise reference - either from master_exercises or user_exercises
  exerciseId: varchar("exercise_id").notNull(), // ID of the exercise (master or user)
  exerciseSource: varchar("exercise_source").notNull().default("master"), // "master" or "user"
  exerciseName: varchar("exercise_name").notNull(), // Cached name for display/queries
  
  // Optional program/session context (null for standalone sets)
  programId: varchar("program_id"),
  programName: varchar("program_name"),
  sessionName: varchar("session_name"), // "Push Day", "Upper Body", etc.
  workoutInstanceId: varchar("workout_instance_id"), // Links to workout_instances table
  
  // Workout date and timing
  workoutDate: varchar("workout_date").notNull(), // YYYY-MM-DD format (user's local date)
  loggedAt: timestamp("logged_at").defaultNow(), // Precise timestamp
  
  // Set details
  setNumber: integer("set_number").notNull(),
  
  // Equipment used for THIS set (key change from old model)
  equipment: varchar("equipment").notNull().default("bodyweight"),
  
  // Strength tracking
  weight: real("weight"),
  weightUnit: varchar("weight_unit").notNull().default("lbs"),
  reps: integer("reps"),
  targetReps: varchar("target_reps"), // "8-12", "10", "AMRAP"
  completed: boolean("completed").notNull().default(false),
  
  // Advanced metrics
  rpe: real("rpe"), // Rate of Perceived Exertion (1-10)
  rir: integer("rir"), // Reps In Reserve
  isWarmup: boolean("is_warmup").default(false), // Whether this is a warm-up set (skip RIR tracking)
  tempo: varchar("tempo"), // e.g., "3-1-2-0" (eccentric-pause-concentric-pause)
  
  // Cardio tracking (for cardio exercises)
  distance: real("distance"),
  distanceUnit: varchar("distance_unit").default("km"),
  durationSeconds: integer("duration_seconds"),
  paceSeconds: integer("pace_seconds"),
  
  // Time tracking (for timed exercises like planks)
  holdSeconds: integer("hold_seconds"),
  
  // Notes for this specific set
  notes: text("notes"),
  
  // Barbell entry mode (for calculating actual weight)
  barbellEntryMode: varchar("barbell_entry_mode"), // "total", "total_added", "per_side"
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("IDX_workout_sets_user").on(table.userId),
  index("IDX_workout_sets_user_date").on(table.userId, table.workoutDate),
  index("IDX_workout_sets_exercise").on(table.userId, table.exerciseId, table.exerciseSource),
  index("IDX_workout_sets_program").on(table.userId, table.programId),
  index("IDX_workout_sets_instance").on(table.workoutInstanceId),
  index("IDX_workout_sets_exercise_equipment").on(table.userId, table.exerciseId, table.equipment),
  index("IDX_workout_sets_logged_at").on(table.userId, table.loggedAt),
]);

export const insertWorkoutSetSchema = createInsertSchema(workoutSets).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true 
});
export type InsertWorkoutSet = z.infer<typeof insertWorkoutSetSchema>;
export type WorkoutSet = typeof workoutSets.$inferSelect;

export const workoutSetsRelations = relations(workoutSets, ({ one }) => ({
  user: one(users, {
    fields: [workoutSets.userId],
    references: [users.id],
  }),
  masterExercise: one(masterExercises, {
    fields: [workoutSets.exerciseId],
    references: [masterExercises.id],
  }),
  userExercise: one(userExercises, {
    fields: [workoutSets.exerciseId],
    references: [userExercises.id],
  }),
}));

export const importedWorkoutLogs = pgTable("imported_workout_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  source: varchar("source").notNull(), // e.g., "strong", "hevy", "fitnotes", "csv", "manual"
  workoutDate: timestamp("workout_date").notNull(),
  workoutName: varchar("workout_name"),
  exerciseName: varchar("exercise_name").notNull(),
  setNumber: integer("set_number").notNull(),
  weight: real("weight"),
  weightUnit: varchar("weight_unit").default("lbs"), // "lbs" or "kg"
  reps: integer("reps"),
  rpe: real("rpe"), // Rate of Perceived Exertion
  notes: text("notes"),
  rawData: jsonb("raw_data"), // store original imported data
  importedAt: timestamp("imported_at").defaultNow(),
}, (table) => [
  index("IDX_imported_workout_logs_user").on(table.userId),
  index("IDX_imported_workout_logs_user_date").on(table.userId, table.workoutDate),
  index("IDX_imported_workout_logs_exercise").on(table.userId, table.exerciseName),
]);

export const TRAINING_BLOCK_CATEGORIES = [
  { id: "general", label: "General Training", description: "Well-rounded training for overall fitness" },
  { id: "hypertrophy", label: "Hypertrophy", description: "Focus on muscle growth and size" },
  { id: "strength", label: "Strength", description: "Building maximal strength and power" },
  { id: "conditioning", label: "Conditioning", description: "Cardiovascular and metabolic training" },
  { id: "athletic", label: "Athletic Performance", description: "Sport-specific performance training" },
  { id: "longevity", label: "Joint Health & Longevity", description: "Mobility, recovery, and long-term health" },
  { id: "rest_recovery", label: "Rest & Recovery", description: "Active recovery, deload, and regeneration" },
  { id: "weight_loss", label: "Weight Loss", description: "Fat burning and caloric deficit training" },
  { id: "maintenance", label: "Maintenance", description: "Maintaining current fitness and strength" },
  { id: "technique", label: "Technique", description: "Form refinement and movement skill development" },
  { id: "other", label: "Other", description: "Custom training focus" },
] as const;

export type TrainingBlockCategory = typeof TRAINING_BLOCK_CATEGORIES[number]["id"];

export const trainingBlockCategorySettings = pgTable("training_block_category_settings", {
  id: varchar("id").primaryKey(),
  label: varchar("label").notNull(),
  description: text("description").notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by").references(() => users.id),
});

export type TrainingBlockCategorySetting = typeof trainingBlockCategorySettings.$inferSelect;
export type InsertTrainingBlockCategorySetting = typeof trainingBlockCategorySettings.$inferInsert;

export const deloadSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  deloadWeeks: z.array(z.number()).default([]),
  weightReduction: z.number().min(0).max(100).default(40),
  repsReduction: z.number().min(0).max(100).default(0),
  setsReduction: z.number().min(0).max(100).default(50),
});

export type DeloadSettings = z.infer<typeof deloadSettingsSchema>;

// Per-program playlist settings for training blocks
export type TrainingBlockProgramPlaylist = {
  programId: string;
  playlistId?: string; // Energy level ID
  playlistUrl?: string; // Custom URL override
};

export const trainingBlocks = pgTable("training_blocks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  name: varchar("name").notNull(),
  category: varchar("category").notNull().default("general"), // training focus category
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date").notNull(),
  status: varchar("status").notNull().default("active"), // "active", "completed", "extended"
  programIds: text("program_ids").array(), // array of program IDs included in this block
  deloadSettings: jsonb("deload_settings").$type<DeloadSettings>(),
  notes: text("notes"),
  // Default playlist for the entire training block (energy level ID)
  defaultPlaylistId: varchar("default_playlist_id"),
  // Per-program playlist overrides
  programPlaylists: jsonb("program_playlists").$type<TrainingBlockProgramPlaylist[]>(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("IDX_training_blocks_user").on(table.userId),
  index("IDX_training_blocks_user_status").on(table.userId, table.status),
  index("IDX_training_blocks_user_dates").on(table.userId, table.startDate, table.endDate),
]);

export const usersRelations = relations(users, ({ many }) => ({
  programs: many(userPrograms),
  workoutLogs: many(workoutLogs),
  workoutSets: many(workoutSets),
  bodyWeightLogs: many(bodyWeightLogs),
  trainingBlocks: many(trainingBlocks),
  musicPreferences: many(musicPreferences),
  userExercises: many(userExercises),
}));

export const trainingBlocksRelations = relations(trainingBlocks, ({ one }) => ({
  user: one(users, {
    fields: [trainingBlocks.userId],
    references: [users.id],
  }),
}));

export const musicPreferences = pgTable("music_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  scopeType: varchar("scope_type").notNull().default("global"), // "global", "program", "trainingBlock", "section"
  scopeId: varchar("scope_id"), // program, training block, or section ID, null for global
  warmupMode: varchar("warmup_mode").notNull().default("curated"), // "curated" or "custom"
  warmupPlaylistUrl: varchar("warmup_playlist_url"),
  warmupEnergyLevelId: varchar("warmup_energy_level_id"), // Energy level ID for curated warmup playlists
  mainMode: varchar("main_mode").notNull().default("curated"), // "curated" or "custom"
  mainPlaylistUrl: varchar("main_playlist_url"),
  mainEnergyLevelId: varchar("main_energy_level_id"), // Energy level ID for curated main workout playlists
  cooldownMode: varchar("cooldown_mode").notNull().default("curated"), // "curated" or "custom"
  cooldownPlaylistUrl: varchar("cooldown_playlist_url"),
  cooldownEnergyLevelId: varchar("cooldown_energy_level_id"), // Energy level ID for curated cooldown playlists
  promptSwitchBack: boolean("prompt_switch_back").notNull().default(true), // Show "switch back to curated" prompt after workout
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("IDX_music_preferences_user").on(table.userId),
  index("IDX_music_preferences_user_scope").on(table.userId, table.scopeType, table.scopeId),
]);

export const curatedPlaylists = pgTable("curated_playlists", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  genre: varchar("genre").notNull(), // matches user musicGenre preference
  name: varchar("name").notNull(),
  spotifyUrl: varchar("spotify_url").notNull(),
  isActive: boolean("is_active").notNull().default(false), // default OFF, admin must enable
});

export const coachProfiles = pgTable("coach_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull().unique(),
  displayName: varchar("display_name").notNull(),
  bio: text("bio"),
  specialties: text("specialties").array(),
  credentials: text("credentials"),
  avatarUrl: varchar("avatar_url"),
  isPublished: boolean("is_published").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const programViews = pgTable("program_views", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  versionGroupId: varchar("version_group_id").notNull(),
  lastViewedProgramId: varchar("last_viewed_program_id").references(() => programs.id).notNull(),
  lastViewedVersion: integer("last_viewed_version").notNull(),
  firstViewedAt: timestamp("first_viewed_at").defaultNow(),
  lastViewedAt: timestamp("last_viewed_at").defaultNow(),
}, (table) => [
  index("IDX_program_views_user").on(table.userId),
  index("IDX_program_views_user_version_group").on(table.userId, table.versionGroupId),
]);

export const coachAssignments = pgTable("coach_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  coachId: varchar("coach_id").references(() => users.id).notNull(),
  customerId: varchar("customer_id").references(() => users.id).notNull(),
  status: varchar("status").notNull().default("active"), // "active", "paused", "ended"
  assignedAt: timestamp("assigned_at").defaultNow(),
  endedAt: timestamp("ended_at"),
}, (table) => [
  index("IDX_coach_assignments_coach_status").on(table.coachId, table.status),
  index("IDX_coach_assignments_customer_status").on(table.customerId, table.status),
  index("IDX_coach_assignments_status").on(table.status),
]);

// Session playlist data for recommendations
export type SessionPlaylistData = {
  sessionId: string;
  sessionName: string;
  playlistId?: string; // Energy level ID
  playlistUrl?: string; // Custom URL override
};

export const coachRecommendations = pgTable("coach_recommendations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  coachId: varchar("coach_id").references(() => users.id).notNull(),
  customerId: varchar("customer_id").references(() => users.id).notNull(),
  type: varchar("type").notNull(), // "program", "trainingBlock", "note", "goal"
  title: varchar("title").notNull(),
  content: text("content"),
  programId: varchar("program_id").references(() => programs.id),
  trainingBlockData: jsonb("training_block_data"), // For training block suggestions: { name, category, weeks, programIds, notes }
  suggestedPlaylistUrl: varchar("suggested_playlist_url"), // Optional Spotify playlist URL suggestion
  suggestedPlaylistId: varchar("suggested_playlist_id"), // Energy level ID for program-level playlist
  // Per-session playlist recommendations
  sessionPlaylists: jsonb("session_playlists").$type<SessionPlaylistData[]>(),
  status: varchar("status").notNull().default("pending"), // "pending", "accepted", "declined", "started"
  declineReason: text("decline_reason"), // Optional feedback from client on why they declined
  isRead: boolean("is_read").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  respondedAt: timestamp("responded_at"),
}, (table) => [
  index("IDX_coach_recommendations_customer").on(table.customerId),
  index("IDX_coach_recommendations_customer_status").on(table.customerId, table.status),
  index("IDX_coach_recommendations_coach").on(table.coachId, table.createdAt),
]);

export const coachProgressReports = pgTable("coach_progress_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").references(() => users.id).notNull(),
  coachId: varchar("coach_id").references(() => users.id).notNull(),
  recommendationId: varchar("recommendation_id").references(() => coachRecommendations.id),
  content: text("content").notNull(),
  metrics: jsonb("metrics"), // Optional: { weight, reps, notes, etc. }
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_coach_progress_reports_customer").on(table.customerId, table.createdAt),
  index("IDX_coach_progress_reports_coach").on(table.coachId, table.createdAt),
]);

// ============================================================
// NEW TABLES FOR DATABASE PORTABILITY (localStorage migration)
// ============================================================

// Store user's 1 Rep Max records (currently in localStorage)
export const oneRepMaxLogs = pgTable("one_rep_max_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  exerciseName: varchar("exercise_name").notNull(),
  weight: real("weight").notNull(),
  unit: varchar("unit").notNull().default("kg"), // "kg" or "lbs"
  loggedAt: timestamp("logged_at").defaultNow(),
  notes: text("notes"),
}, (table) => [
  index("IDX_one_rep_max_logs_user").on(table.userId),
  index("IDX_one_rep_max_logs_exercise").on(table.userId, table.exerciseName),
]);

// Store user's cardio personal records (fastest times, longest distances, etc.)
export const cardioPersonalRecords = pgTable("cardio_personal_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  exerciseName: varchar("exercise_name").notNull(), // "Running", "Cycling", "Rowing", etc.
  recordType: varchar("record_type").notNull(), // "fastest_time", "longest_distance", "best_pace"
  distance: real("distance"), // in km (for time-based records)
  duration: integer("duration"), // in seconds (for distance-based or time records)
  pace: real("pace"), // minutes per km/mi
  speed: real("speed"), // km/h or mph
  distanceUnit: varchar("distance_unit").notNull().default("km"), // "km" or "mi"
  loggedAt: timestamp("logged_at").defaultNow(),
  notes: text("notes"),
}, (table) => [
  index("IDX_cardio_pr_user").on(table.userId),
  index("IDX_cardio_pr_exercise").on(table.userId, table.exerciseName),
]);

// Store user's tracked exercises for progress charts (currently in localStorage)
export const userTrackedExercises = pgTable("user_tracked_exercises", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  exerciseId: varchar("exercise_id").notNull(), // local identifier like "squat", "bench", etc.
  exerciseName: varchar("exercise_name").notNull(),
  color: varchar("color").notNull().default("#3b82f6"),
  mode: varchar("mode").notNull().default("max"), // "max", "lastSet", or "1rm"
  equipment: varchar("equipment").default("all"), // "all", "barbell", "dumbbell", "machine", "cable", "bodyweight"
  displayOrder: integer("display_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_user_tracked_exercises_user").on(table.userId),
]);

// Store user's tracked cardio exercises for progress charts
export const userTrackedCardioExercises = pgTable("user_tracked_cardio_exercises", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  exerciseId: varchar("exercise_id").notNull(), // local identifier like "running", "cycling", etc.
  exerciseName: varchar("exercise_name").notNull(),
  color: varchar("color").notNull().default("#10b981"),
  displayOrder: integer("display_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_user_tracked_cardio_exercises_user").on(table.userId),
]);

// Store per-program workout progress (currently in localStorage as programProgress_*)
export const userWorkoutProgress = pgTable("user_workout_progress", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  programId: varchar("program_id").notNull(), // can be expert program ID or custom program ID
  currentWeek: integer("current_week").notNull().default(1),
  currentWorkoutIndex: integer("current_workout_index").notNull().default(0),
  progressData: jsonb("progress_data"), // stores exercise logs, set data, etc.
  lastUpdatedAt: timestamp("last_updated_at").defaultNow(),
}, (table) => [
  index("IDX_user_workout_progress_user").on(table.userId),
  uniqueIndex("IDX_user_workout_progress_unique").on(table.userId, table.programId),
]);

// Store completed workout sessions with metadata
export const workoutSessions = pgTable("workout_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  programId: varchar("program_id").notNull(),
  programTitle: varchar("program_title").notNull(),
  workoutName: varchar("workout_name").notNull(),
  week: integer("week").notNull(),
  workoutIndex: integer("workout_index").notNull(),
  startedAt: timestamp("started_at").notNull(),
  completedAt: timestamp("completed_at"),
  completionDate: varchar("completion_date"), // client's local date (YYYY-MM-DD) for timezone-safe display
  durationSeconds: integer("duration_seconds"),
  status: varchar("status").notNull().default("in_progress"), // "in_progress", "completed", "abandoned"
  exerciseData: jsonb("exercise_data"), // detailed exercise logs for this session
}, (table) => [
  index("IDX_workout_sessions_user").on(table.userId),
  index("IDX_workout_sessions_user_program").on(table.userId, table.programId),
  index("IDX_workout_sessions_date").on(table.userId, table.startedAt),
]);

// Store workout schedule/calendar entries
export const workoutSchedule = pgTable("workout_schedule", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  scheduledDate: varchar("scheduled_date").notNull(),
  programId: varchar("program_id").notNull(),
  workoutIndex: integer("workout_index").notNull(),
  week: integer("week").notNull(),
  workoutName: varchar("workout_name").notNull(),
  status: varchar("status").notNull().default("scheduled"), // "scheduled", "completed", "skipped", "rescheduled"
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_workout_schedule_user").on(table.userId),
  index("IDX_workout_schedule_date").on(table.userId, table.scheduledDate),
]);

// Relations for new tables
export const oneRepMaxLogsRelations = relations(oneRepMaxLogs, ({ one }) => ({
  user: one(users, {
    fields: [oneRepMaxLogs.userId],
    references: [users.id],
  }),
}));

export const cardioPersonalRecordsRelations = relations(cardioPersonalRecords, ({ one }) => ({
  user: one(users, {
    fields: [cardioPersonalRecords.userId],
    references: [users.id],
  }),
}));

export const userTrackedExercisesRelations = relations(userTrackedExercises, ({ one }) => ({
  user: one(users, {
    fields: [userTrackedExercises.userId],
    references: [users.id],
  }),
}));

export const userWorkoutProgressRelations = relations(userWorkoutProgress, ({ one }) => ({
  user: one(users, {
    fields: [userWorkoutProgress.userId],
    references: [users.id],
  }),
}));

export const workoutSessionsRelations = relations(workoutSessions, ({ one }) => ({
  user: one(users, {
    fields: [workoutSessions.userId],
    references: [users.id],
  }),
}));

export const workoutScheduleRelations = relations(workoutSchedule, ({ one }) => ({
  user: one(users, {
    fields: [workoutSchedule.userId],
    references: [users.id],
  }),
}));

// Admin broadcast messages (popup warnings before deployments, etc.)
export const broadcastMessages = pgTable("broadcast_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  message: text("message").notNull(),
  type: varchar("type").notNull().default("warning"), // "warning", "info", "maintenance"
  isActive: boolean("is_active").notNull().default(true),
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  expiresAt: timestamp("expires_at"), // Optional auto-expiry
});

export const insertBroadcastMessageSchema = createInsertSchema(broadcastMessages).omit({
  id: true,
  createdAt: true,
});
export type InsertBroadcastMessage = z.infer<typeof insertBroadcastMessageSchema>;
export type BroadcastMessage = typeof broadcastMessages.$inferSelect;

// ============================================================
// WORKOUT INSTANCES - Date-centric workout logging
// ============================================================

// Workout instances represent a specific workout logged on a specific date
// They can be tied to a program template (programId + week + workoutIndex) or standalone
export const WORKOUT_INSTANCE_STATUSES = ['in_progress', 'completed', 'abandoned'] as const;
export type WorkoutInstanceStatus = typeof WORKOUT_INSTANCE_STATUSES[number];

// Session types for type-aware navigation (e.g., "Previous Upper Body")
export const SESSION_TYPES = ['upper', 'lower', 'push', 'pull', 'legs', 'full_body', 'chest', 'back', 'shoulders', 'arms', 'core', 'cardio', 'quick_log', 'custom'] as const;
export type SessionType = typeof SESSION_TYPES[number];

export const workoutInstances = pgTable(
  'workout_instances',
  {
    id: uuid('id').primaryKey().defaultRandom(),

    userId: uuid('user_id').notNull(),
    programId: uuid('program_id').notNull(),

    logDate: date('log_date').notNull(),
    sessionType: text('session_type').notNull(),

    templateWeek: integer('template_week'),
    templateWorkoutIndex: integer('template_workout_index'),

    status: text('status').default('in_progress'),
    startedAt: timestamp('started_at'),
    completedAt: timestamp('completed_at'),
    durationSeconds: integer('duration_seconds'),


    workoutName: text('workout_name'),
    programTitle: text('program_title'),

    exerciseData: jsonb('exercise_data'),
    notes: text('notes'),

    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  (table) => ({
    uniqUserProgramDateSession: uniqueIndex(
      'wi_unique_user_program_date_session_type'
    ).on(
      table.userId,
      table.programId,
      table.logDate,
      table.sessionType
    ),
  })
);


export const workoutInstancesRelations = relations(workoutInstances, ({ one }) => ({
  user: one(users, {
    fields: [workoutInstances.userId],
    references: [users.id],
  }),
}));

export type WorkoutInstance = typeof workoutInstances.$inferSelect;
export type InsertWorkoutInstance = typeof workoutInstances.$inferInsert;
export const insertWorkoutInstanceSchema = createInsertSchema(workoutInstances).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true 
});
export const updateWorkoutInstanceSchema = createInsertSchema(workoutInstances).omit({ 
  id: true, 
  userId: true, 
  createdAt: true, 
  updatedAt: true 
}).partial();

// ============================================================
// END NEW TABLES
// ============================================================

export const coachAssignmentsRelations = relations(coachAssignments, ({ one }) => ({
  coach: one(users, {
    fields: [coachAssignments.coachId],
    references: [users.id],
    relationName: "coachAssignments",
  }),
  customer: one(users, {
    fields: [coachAssignments.customerId],
    references: [users.id],
    relationName: "customerAssignments",
  }),
}));

export const coachRecommendationsRelations = relations(coachRecommendations, ({ one }) => ({
  coach: one(users, {
    fields: [coachRecommendations.coachId],
    references: [users.id],
    relationName: "coachRecommendations",
  }),
  customer: one(users, {
    fields: [coachRecommendations.customerId],
    references: [users.id],
    relationName: "customerRecommendations",
  }),
  program: one(programs, {
    fields: [coachRecommendations.programId],
    references: [programs.id],
  }),
}));

export const musicPreferencesRelations = relations(musicPreferences, ({ one }) => ({
  user: one(users, {
    fields: [musicPreferences.userId],
    references: [users.id],
  }),
}));

export const programsRelations = relations(programs, ({ many, one }) => ({
  sections: many(workoutSections),
  userPrograms: many(userPrograms),
  createdByUser: one(users, {
    fields: [programs.createdBy],
    references: [users.id],
  }),
}));

export const workoutSectionsRelations = relations(workoutSections, ({ one, many }) => ({
  program: one(programs, {
    fields: [workoutSections.programId],
    references: [programs.id],
  }),
  exercises: many(exercises),
}));

export const exercisesRelations = relations(exercises, ({ one, many }) => ({
  section: one(workoutSections, {
    fields: [exercises.sectionId],
    references: [workoutSections.id],
  }),
  logs: many(workoutLogs),
}));

export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;
export type Program = typeof programs.$inferSelect;
export type InsertProgram = typeof programs.$inferInsert;
export type WorkoutSection = typeof workoutSections.$inferSelect;
export type InsertWorkoutSection = typeof workoutSections.$inferInsert;
export type Exercise = typeof exercises.$inferSelect;
export type InsertExercise = typeof exercises.$inferInsert;
export type UserProgram = typeof userPrograms.$inferSelect;
export type InsertUserProgram = typeof userPrograms.$inferInsert;
export type WorkoutLog = typeof workoutLogs.$inferSelect;
export type InsertWorkoutLog = typeof workoutLogs.$inferInsert;
export type BodyWeightLog = typeof bodyWeightLogs.$inferSelect;
export type InsertBodyWeightLog = typeof bodyWeightLogs.$inferInsert;
export type ImportedWorkoutLog = typeof importedWorkoutLogs.$inferSelect;
export type InsertImportedWorkoutLog = typeof importedWorkoutLogs.$inferInsert;
export type TrainingBlock = typeof trainingBlocks.$inferSelect;
export type InsertTrainingBlock = typeof trainingBlocks.$inferInsert;
export type MusicPreference = typeof musicPreferences.$inferSelect;
export type InsertMusicPreference = typeof musicPreferences.$inferInsert;
export type CuratedPlaylist = typeof curatedPlaylists.$inferSelect;
export type InsertCuratedPlaylist = typeof curatedPlaylists.$inferInsert;
export type CoachAssignment = typeof coachAssignments.$inferSelect;
export type InsertCoachAssignment = typeof coachAssignments.$inferInsert;
export type CoachRecommendation = typeof coachRecommendations.$inferSelect;
export type InsertCoachRecommendation = typeof coachRecommendations.$inferInsert;
export type CoachProgressReport = typeof coachProgressReports.$inferSelect;
export type InsertCoachProgressReport = typeof coachProgressReports.$inferInsert;
export type AccessCode = typeof accessCodes.$inferSelect;
export type InsertAccessCode = typeof accessCodes.$inferInsert;
export type AccessCodeRedemption = typeof accessCodeRedemptions.$inferSelect;
export type InsertAccessCodeRedemption = typeof accessCodeRedemptions.$inferInsert;
export type CoachProfile = typeof coachProfiles.$inferSelect;
export type InsertCoachProfile = typeof coachProfiles.$inferInsert;
export type ProgramView = typeof programViews.$inferSelect;
export type InsertProgramView = typeof programViews.$inferInsert;

// New portable tables types
export type OneRepMaxLog = typeof oneRepMaxLogs.$inferSelect;
export type InsertOneRepMaxLog = typeof oneRepMaxLogs.$inferInsert;
export type CardioPersonalRecord = typeof cardioPersonalRecords.$inferSelect;
export type InsertCardioPersonalRecord = typeof cardioPersonalRecords.$inferInsert;
export type UserTrackedExercise = typeof userTrackedExercises.$inferSelect;
export type InsertUserTrackedExercise = typeof userTrackedExercises.$inferInsert;
export type UserTrackedCardioExercise = typeof userTrackedCardioExercises.$inferSelect;
export type InsertUserTrackedCardioExercise = typeof userTrackedCardioExercises.$inferInsert;
export type UserWorkoutProgress = typeof userWorkoutProgress.$inferSelect;
export type InsertUserWorkoutProgress = typeof userWorkoutProgress.$inferInsert;
export type WorkoutSession = typeof workoutSessions.$inferSelect;
export type InsertWorkoutSession = typeof workoutSessions.$inferInsert;
export type WorkoutScheduleEntry = typeof workoutSchedule.$inferSelect;
export type InsertWorkoutScheduleEntry = typeof workoutSchedule.$inferInsert;

export const insertProgramSchema = createInsertSchema(programs).omit({ id: true, createdAt: true, updatedAt: true });
export const insertWorkoutSectionSchema = createInsertSchema(workoutSections).omit({ id: true });
export const insertExerciseSchema = createInsertSchema(exercises).omit({ id: true });
export const insertUserProgramSchema = createInsertSchema(userPrograms).omit({ id: true, startedAt: true, completedAt: true });
export const insertWorkoutLogSchema = createInsertSchema(workoutLogs).omit({ id: true, userId: true, loggedAt: true });
export const insertBodyWeightLogSchema = createInsertSchema(bodyWeightLogs).omit({ id: true, userId: true, loggedAt: true });
export const insertImportedWorkoutLogSchema = createInsertSchema(importedWorkoutLogs).omit({ id: true, importedAt: true });
export const insertTrainingBlockSchema = createInsertSchema(trainingBlocks).omit({ id: true, createdAt: true, updatedAt: true });
export const updateTrainingBlockSchema = createInsertSchema(trainingBlocks).omit({ id: true, userId: true, createdAt: true, updatedAt: true }).partial();
export const insertMusicPreferenceSchema = createInsertSchema(musicPreferences).omit({ id: true, updatedAt: true });
export const updateMusicPreferenceSchema = createInsertSchema(musicPreferences).omit({ id: true, userId: true, updatedAt: true }).partial();
export const insertCuratedPlaylistSchema = createInsertSchema(curatedPlaylists).omit({ id: true });
export const insertCoachAssignmentSchema = createInsertSchema(coachAssignments).omit({ id: true, assignedAt: true, endedAt: true });
export const insertCoachRecommendationSchema = createInsertSchema(coachRecommendations).omit({ id: true, createdAt: true, isRead: true, status: true, respondedAt: true });
export const insertCoachProgressReportSchema = createInsertSchema(coachProgressReports).omit({ id: true, createdAt: true });
export const insertCoachProfileSchema = createInsertSchema(coachProfiles).omit({ id: true, createdAt: true, updatedAt: true });
export const updateCoachProfileSchema = createInsertSchema(coachProfiles).omit({ id: true, userId: true, createdAt: true, updatedAt: true }).partial();
export const insertProgramViewSchema = createInsertSchema(programViews).omit({ id: true, firstViewedAt: true, lastViewedAt: true });

// New portable tables schemas
export const insertOneRepMaxLogSchema = createInsertSchema(oneRepMaxLogs).omit({ id: true, loggedAt: true });
export const insertCardioPersonalRecordSchema = createInsertSchema(cardioPersonalRecords).omit({ id: true, loggedAt: true });
export const insertUserTrackedExerciseSchema = createInsertSchema(userTrackedExercises).omit({ id: true, createdAt: true });
export const insertUserTrackedCardioExerciseSchema = createInsertSchema(userTrackedCardioExercises).omit({ id: true, createdAt: true });
export const insertUserWorkoutProgressSchema = createInsertSchema(userWorkoutProgress).omit({ id: true, lastUpdatedAt: true });
export const updateUserWorkoutProgressSchema = createInsertSchema(userWorkoutProgress).omit({ id: true, userId: true, programId: true, lastUpdatedAt: true }).partial();
export const insertWorkoutSessionSchema = createInsertSchema(workoutSessions).omit({ id: true });
export const updateWorkoutSessionSchema = createInsertSchema(workoutSessions).omit({ id: true, userId: true, programId: true }).partial();
export const insertWorkoutScheduleSchema = createInsertSchema(workoutSchedule).omit({ id: true, createdAt: true });

// Issue Reports for user feedback
export const ISSUE_TYPES = ['app_didnt_work', 'music_issue', 'ui_confusing', 'other'] as const;
export type IssueType = typeof ISSUE_TYPES[number];

export const ISSUE_STATUSES = ['open', 'archived'] as const;
export type IssueStatus = typeof ISSUE_STATUSES[number];

export const issueReports = pgTable("issue_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  issueType: varchar("issue_type").notNull(),
  description: text("description"),
  currentPage: varchar("current_page"),
  deviceType: varchar("device_type"),
  browser: varchar("browser"),
  status: varchar("status").default("open").notNull(),
  flagged: boolean("flagged").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_issue_reports_user").on(table.userId),
  index("IDX_issue_reports_created_at").on(table.createdAt),
  index("IDX_issue_reports_status").on(table.status),
]);

export type IssueReport = typeof issueReports.$inferSelect;
export type InsertIssueReport = typeof issueReports.$inferInsert;
export const insertIssueReportSchema = createInsertSchema(issueReports).omit({ id: true, createdAt: true });

// Error Reports for automatic error logging
export const ERROR_SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type ErrorSeverity = typeof ERROR_SEVERITIES[number];

export const errorReports = pgTable("error_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id),
  errorMessage: text("error_message").notNull(),
  stackTrace: text("stack_trace"),
  route: varchar("route"),
  componentName: varchar("component_name"),
  userAgent: varchar("user_agent"),
  severity: varchar("severity").default("medium").notNull(),
  isResolved: boolean("is_resolved").default(false).notNull(),
  flagged: boolean("flagged").default(false).notNull(),
  resolvedAt: timestamp("resolved_at"),
  resolvedBy: varchar("resolved_by"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_error_reports_user").on(table.userId),
  index("IDX_error_reports_created_at").on(table.createdAt),
  index("IDX_error_reports_resolved").on(table.isResolved),
  index("IDX_error_reports_severity").on(table.severity),
  index("IDX_error_reports_flagged").on(table.flagged),
]);

export type ErrorReport = typeof errorReports.$inferSelect;
export type InsertErrorReport = typeof errorReports.$inferInsert;
export const insertErrorReportSchema = createInsertSchema(errorReports).omit({ id: true, createdAt: true, isResolved: true, resolvedAt: true, resolvedBy: true });

// In-app notifications
export const NOTIFICATION_TYPES = ['trial_ending', 'subscription_active', 'payment_failed', 'general'] as const;
export type NotificationType = typeof NOTIFICATION_TYPES[number];

export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  type: varchar("type").notNull(),
  title: varchar("title").notNull(),
  message: text("message").notNull(),
  isRead: boolean("is_read").notNull().default(false),
  isDismissed: boolean("is_dismissed").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_notifications_user").on(table.userId),
  index("IDX_notifications_user_read").on(table.userId, table.isRead),
]);

export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = typeof notifications.$inferInsert;
export const insertNotificationSchema = createInsertSchema(notifications).omit({ id: true, createdAt: true, isRead: true, isDismissed: true });

export const completedExerciseSchema = z.object({
  name: z.string(),
  sets: z.number(),
  reps: z.string(),
  logs: z.any().optional()
});

export const completedWorkoutSchema = z.object({
  date: z.string(),
  programId: z.string(),
  programName: z.string(),
  workoutName: z.string(),
  week: z.number().optional(),
  workoutIndex: z.number().optional(),
  exercises: z.array(completedExerciseSchema).optional()
});

export const exportWorkoutsSchema = z.object({
  workouts: z.array(completedWorkoutSchema).min(1)
});

export type CompletedWorkoutExport = z.infer<typeof completedWorkoutSchema>;

// OAuth 2.0 tables for third-party integrations
export const oauthClients = pgTable("oauth_clients", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  clientId: varchar("client_id").notNull().unique(),
  clientSecret: varchar("client_secret").notNull(),
  name: varchar("name").notNull(),
  description: text("description"),
  redirectUris: text("redirect_uris").array().notNull(),
  scopes: text("scopes").array().notNull().default(sql`ARRAY['read']::text[]`),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type OAuthClient = typeof oauthClients.$inferSelect;
export type InsertOAuthClient = typeof oauthClients.$inferInsert;

export const oauthAuthorizationCodes = pgTable("oauth_authorization_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: varchar("code").notNull().unique(),
  clientId: varchar("client_id").references(() => oauthClients.clientId).notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  redirectUri: varchar("redirect_uri").notNull(),
  scope: varchar("scope").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_oauth_codes_code").on(table.code),
  index("IDX_oauth_codes_expires").on(table.expiresAt),
]);

export type OAuthAuthorizationCode = typeof oauthAuthorizationCodes.$inferSelect;

export const oauthTokens = pgTable("oauth_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  accessToken: varchar("access_token").notNull().unique(),
  refreshToken: varchar("refresh_token").unique(),
  clientId: varchar("client_id").references(() => oauthClients.clientId).notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  scope: varchar("scope").notNull(),
  accessTokenExpiresAt: timestamp("access_token_expires_at").notNull(),
  refreshTokenExpiresAt: timestamp("refresh_token_expires_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_oauth_tokens_access").on(table.accessToken),
  index("IDX_oauth_tokens_refresh").on(table.refreshToken),
  index("IDX_oauth_tokens_user").on(table.userId),
]);

export type OAuthToken = typeof oauthTokens.$inferSelect;

// Energy Levels for music/workout matching (Recommended Playlists)
export const energyLevels = pgTable("energy_levels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name").notNull().unique(),
  spotifySearchQuery: varchar("spotify_search_query"), // Legacy: Optional custom Spotify search query
  spotifyUrl: varchar("spotify_url"), // Direct Spotify playlist URL (preferred)
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(false), // Default OFF - admin must enable
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertEnergyLevelSchema = createInsertSchema(energyLevels).omit({ id: true, createdAt: true });
export type InsertEnergyLevel = z.infer<typeof insertEnergyLevelSchema>;
export type EnergyLevel = typeof energyLevels.$inferSelect;

// Push Subscriptions for Web Push Notifications
export const pushSubscriptions = pgTable("push_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  endpoint: text("endpoint").notNull(),
  p256dh: text("p256dh").notNull(),
  auth: text("auth").notNull(),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("IDX_push_subscriptions_user_id").on(table.userId),
  uniqueIndex("IDX_push_subscriptions_endpoint").on(table.endpoint),
]);

export const insertPushSubscriptionSchema = createInsertSchema(pushSubscriptions).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPushSubscription = z.infer<typeof insertPushSubscriptionSchema>;
export type PushSubscription = typeof pushSubscriptions.$inferSelect;

// Scheduled Push Notifications (durable storage for serverless environments)
export const NOTIFICATION_STATUSES = ['pending', 'sent', 'cancelled', 'failed'] as const;
export type NotificationStatus = typeof NOTIFICATION_STATUSES[number];

export const scheduledNotifications = pgTable("scheduled_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  type: varchar("type").notNull().default("rest-timer"), // 'rest-timer', 'workout-reminder', etc.
  tag: varchar("tag").notNull(), // Unique tag for this notification type per user
  fireAt: timestamp("fire_at").notNull(), // When to send the notification
  status: varchar("status").notNull().default("pending"),
  title: varchar("title").notNull(),
  body: text("body").notNull(),
  returnUrl: text("return_url"),
  attempts: integer("attempts").notNull().default(0),
  sentAt: timestamp("sent_at"),
  cancelledAt: timestamp("cancelled_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_scheduled_notifications_user_id").on(table.userId),
  index("IDX_scheduled_notifications_status_fire_at").on(table.status, table.fireAt),
  index("IDX_scheduled_notifications_user_tag").on(table.userId, table.tag),
]);

export const insertScheduledNotificationSchema = createInsertSchema(scheduledNotifications).omit({ id: true, createdAt: true, sentAt: true, cancelledAt: true, attempts: true });
export type InsertScheduledNotification = z.infer<typeof insertScheduledNotificationSchema>;
export type ScheduledNotification = typeof scheduledNotifications.$inferSelect;

// Active Workout Sessions - Track ongoing workouts for idle notifications
export const ACTIVE_SESSION_STATUSES = ['active', 'notified', 'cleared'] as const;
export type ActiveSessionStatus = typeof ACTIVE_SESSION_STATUSES[number];

export const activeWorkoutSessions = pgTable("active_workout_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  workoutInstanceId: varchar("workout_instance_id"), // Reference to workout_instances if applicable
  programId: varchar("program_id"), // Snapshot of program being worked on
  workoutIndex: integer("workout_index"), // Snapshot of workout index
  workoutName: varchar("workout_name"), // Snapshot for notification context
  source: varchar("source").notNull().default("program"), // "program" or "quick"
  startedAt: timestamp("started_at").notNull().defaultNow(),
  lastHeartbeatAt: timestamp("last_heartbeat_at").notNull().defaultNow(),
  status: varchar("status").notNull().default("active"),
  notificationSentAt: timestamp("notification_sent_at"),
}, (table) => [
  index("IDX_active_workout_sessions_user_id").on(table.userId),
  index("IDX_active_workout_sessions_status").on(table.status),
  index("IDX_active_workout_sessions_heartbeat").on(table.status, table.lastHeartbeatAt),
]);

export const insertActiveWorkoutSessionSchema = createInsertSchema(activeWorkoutSessions).omit({ id: true, startedAt: true, lastHeartbeatAt: true, notificationSentAt: true });
export type InsertActiveWorkoutSession = z.infer<typeof insertActiveWorkoutSessionSchema>;
export type ActiveWorkoutSession = typeof activeWorkoutSessions.$inferSelect;
